import React, { useState, useMemo, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { Expense } from '../../types';
import api from '../../services/api';
import { getManualEditsForExpense } from '../../utils/manualEditTracker';
import { getSubcategories, findCategoryByName } from '../../services/categoryService';
import { FlatCategory } from '../../types/category';
import { FIELD_LABELS, getInvoiceNumber, getCriticalMissingFields, getSoftMissingFields } from '../../utils/fieldLabels';
import { saveTableNavList } from '../../utils/tableNavContext';
import { shouldLoadAnotherExpensePage } from '../../utils/expensePages';
import { formatMoney, formatMoneyPlain, roundMoney } from '../../utils/formatMoney';
import { currencyByCode, formatForeignAmount, isShekel, optionalNumber } from '../../utils/foreignCurrency';
import { formatMetricaDate } from '../../utils/dateUtils';
import { NO_VAT_DISPLAY } from '../../utils/noVat';
import { amountForFilter, isCreditDocumentType, matchesCreditFilter } from '../../utils/creditNotes';
import { isExpenseReviewed } from '../../utils/reviewedExpenses';
import { useToast } from '../../context/ToastContext';
import { useCpa } from '../../context/CpaContext';
import AlertDialog from '../AlertDialog/AlertDialog';
import {
  canShowRecurrenceReminder,
  markRecurrenceReminderShown,
} from '../../utils/recurrenceSeries';
import { ExpenseReviewStatusDot } from '../ExpenseReviewStatusDot/ExpenseReviewStatusDot';
import ReviewStatusFilterDropdown from '../ReviewStatusFilterDropdown/ReviewStatusFilterDropdown';
import SearchableSelect, { optionsFromNames } from '../SearchableSelect/SearchableSelect';
import {
  expenseMatchesReviewStatusFilterSet,
  amountNeedsReviewFromLevel,
  ReviewStatusDotState,
} from '../../utils/reviewStatusDot';
import styles from './ExpenseTable.module.css';

let recurrenceReminderHandledThisLoad = false;

/** Power BI–style searchable vendor dropdown for docked filters. */
const VendorSearchCombobox: React.FC<{
  value: string;
  options: string[];
  onChange: (value: string) => void;
  'aria-label'?: string;
}> = ({ value, options, onChange, 'aria-label': ariaLabel }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((vendor) => vendor.toLowerCase().includes(q));
  }, [options, query]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  useEffect(() => {
    if (open) {
      inputRef.current?.focus();
    }
  }, [open]);

  const selectValue = (next: string) => {
    onChange(next);
    setOpen(false);
    setQuery('');
  };

  const toggleOpen = () => {
    setOpen((prev) => {
      const next = !prev;
      if (!next) setQuery('');
      return next;
    });
  };

  return (
    <div className={styles.vendorCombobox} ref={rootRef}>
      <button
        type="button"
        className={styles.vendorComboboxTrigger}
        onClick={toggleOpen}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
      >
        <span className={styles.vendorComboboxValue}>{value || 'הכל'}</span>
      </button>

      {open && (
        <div className={styles.vendorComboboxPanel} role="listbox" aria-label={ariaLabel}>
          <input
            ref={inputRef}
            type="text"
            className={styles.vendorComboboxSearch}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="חפש ספק..."
            aria-label="חפש ספק"
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setOpen(false);
                setQuery('');
              } else if (e.key === 'Enter') {
                e.preventDefault();
                if (filteredOptions.length === 1) {
                  selectValue(filteredOptions[0]);
                } else if (query.trim() === '') {
                  selectValue('');
                }
              }
            }}
          />
          <button
            type="button"
            className={`${styles.vendorComboboxOption} ${!value ? styles.vendorComboboxOptionActive : ''}`.trim()}
            role="option"
            aria-selected={!value}
            onClick={() => selectValue('')}
          >
            הכל
          </button>
          <div className={styles.vendorComboboxList}>
            {filteredOptions.length === 0 ? (
              <div className={styles.vendorComboboxEmpty}>אין תוצאות</div>
            ) : (
              filteredOptions.map((vendor) => (
                <button
                  key={vendor}
                  type="button"
                  className={`${styles.vendorComboboxOption} ${value === vendor ? styles.vendorComboboxOptionActive : ''}`.trim()}
                  role="option"
                  aria-selected={value === vendor}
                  onClick={() => selectValue(vendor)}
                >
                  {vendor}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};


/**
 * Column 13 status dot — red / green / pink (L2 missing) / grey.
 */
const ReviewStatusDot: React.FC<{ expense: Expense }> = ({ expense }) => (
  <ExpenseReviewStatusDot
    expense={expense}
    reviewed={isExpenseReviewed(expense.id)}
    variant="table"
  />
);

// Manual Edit Indicator Component (for future use)
// eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any
const ManualEditIndicator: React.FC = () => {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div className={styles.manualEditIndicator}>
      <div 
        className={styles.triangle}
        onClick={() => setShowTooltip(!showTooltip)}
      >
        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2L2 22h20L12 2z" fill="currentColor"/>
        </svg>
      </div>
      {showTooltip && (
        <div className={styles.clickTooltip}>
          ערוך באופן ידני
        </div>
      )}
    </div>
  );
};

// Truncate to cell width; show full text on hover when overflowed
const TruncatedCellText: React.FC<{
  text?: string | null;
  className?: string;
  dir?: 'ltr' | 'rtl' | 'auto';
}> = ({ text, className, dir }) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const value = text?.toString() ?? '';

  const measureTruncation = () => {
    const el = textRef.current;
    if (!el) {
      setIsTruncated(false);
      return;
    }
    setIsTruncated(el.scrollWidth > el.clientWidth + 1);
  };

  const clearHoverTimer = () => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  };

  useEffect(() => {
    measureTruncation();
    window.addEventListener('resize', measureTruncation);
    return () => {
      window.removeEventListener('resize', measureTruncation);
      clearHoverTimer();
    };
  }, [value]);

  if (!value) {
    return <div className={styles.cellContent} />;
  }

  return (
    <div
      className={styles.cellContent}
      onMouseEnter={() => {
        measureTruncation();
        clearHoverTimer();
        // 100% slower appear: 400ms delay before showing
        hoverTimerRef.current = setTimeout(() => setShowTooltip(true), 400);
      }}
      onMouseLeave={() => {
        clearHoverTimer();
        setShowTooltip(false);
      }}
    >
      <span
        ref={textRef}
        className={`${styles.truncatedText} ${className || ''}`.trim()}
        dir={dir}
      >
        {value}
      </span>
      {showTooltip && isTruncated && (
        <div className={styles.cellFullTextTooltip} role="tooltip">
          {value}
        </div>
      )}
    </div>
  );
};

// VAT mark: grey dot only when vat_percentage is explicitly set in the category tree
const VATEligibilityIndicator: React.FC<{ vatPercentage: number }> = ({ vatPercentage }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipText =
    Number(vatPercentage) === 0 ? 'No VAT' : `${Number(vatPercentage)}% VAT`;

  return (
    <div
      className={styles.vatEligibilityIndicatorContainer}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      aria-label={tooltipText}
    >
      <div className={styles.vatEligibilityIndicator} />
      {showTooltip && <div className={styles.vatTooltip}>{tooltipText}</div>}
    </div>
  );
};

// Pagination Component
const Pagination: React.FC<{
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  totalItems: number;
  itemsPerPage: number;
  startIndex: number;
  endIndex: number;
  rowsPerPage: number;
  onRowsPerPageChange: (rows: number) => void;
  loadProgress: { loaded: number; total: number } | null;
}> = ({
  currentPage,
  totalPages,
  onPageChange,
  totalItems,
  itemsPerPage,
  startIndex,
  endIndex,
  rowsPerPage,
  onRowsPerPageChange,
  loadProgress,
}) => {
  const loadingList = loadProgress !== null;
  const knownTotal = loadingList && loadProgress.total > 0;
  const progressPercent = knownTotal
    ? Math.min(100, Math.round((loadProgress.loaded / loadProgress.total) * 100))
    : 0;

  return (
    <div className={styles.paginationContainer}>
      <div className={styles.paginationInfo}>
        {loadingList ? (
          <div
            className={styles.loadProgressTrack}
            role="progressbar"
            aria-label="טוען"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={knownTotal ? progressPercent : undefined}
          >
            <div
              className={`${styles.loadProgressFill} ${knownTotal ? '' : styles.loadProgressFillIndeterminate}`.trim()}
              style={knownTotal ? { width: `${progressPercent}%` } : undefined}
            />
          </div>
        ) : (
          <span>
            מציג {startIndex + 1}-{Math.min(endIndex, totalItems)} מתוך {totalItems} שורות
          </span>
        )}
      </div>

      <div className={styles.paginationControls}>
        <button
          className={`${styles.paginationButton} ${styles.paginationButtonFirst}`}
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1 || totalPages <= 1}
          title="עמוד ראשון"
          type="button"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M5.59 16.59L7 18l6-6-6-6-1.41 1.41L10.17 12l-4.58 4.59zM18 6h2v12h-2V6z" fill="currentColor"/>
          </svg>
        </button>

        <button
          className={`${styles.paginationButton} ${styles.paginationButtonPrev}`}
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1 || totalPages <= 1}
          title="עמוד קודם"
          type="button"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12l-4.58 4.59z" fill="currentColor"/>
          </svg>
        </button>

        <div className={styles.rowsPerPageControl}>
          <span className={styles.rowsLabel}>שורות לדף:</span>
          <select
            value={rowsPerPage}
            onChange={(e) => onRowsPerPageChange(Number(e.target.value))}
            className={styles.rowsSelect}
            aria-label="שורות לדף"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={15}>15</option>
            <option value={20}>20</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>

        <button
          className={`${styles.paginationButton} ${styles.paginationButtonNext}`}
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages || totalPages <= 1}
          title="עמוד הבא"
          type="button"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12l4.58-4.59z" fill="currentColor"/>
          </svg>
        </button>

        <button
          className={`${styles.paginationButton} ${styles.paginationButtonLast}`}
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages || totalPages <= 1}
          title="עמוד אחרון"
          type="button"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18.41 7.41L17 6l-6 6 6 6 1.41-1.41L13.83 12l4.58-4.59zM6 6h2v12H6V6z" fill="currentColor"/>
          </svg>
        </button>
      </div>
    </div>
  );
};

// Storage key for table state
const TABLE_STATE_STORAGE_KEY = 'expenseTableState';
const TABLE_SCROLL_STORAGE_KEY = 'expenseTableScroll';
const FILTER_UI_STORAGE_KEY = 'expenseTableFilterUi';
const RELOAD_AFTER_EDIT_KEY = 'expenses-reload-after-edit';

interface FilterUiState {
  showFilters: boolean;
  filtersDocked: boolean;
  filterPanelPos: { left: number; top: number } | null;
  filterPanelSize: { width: number; height: number | null };
}

const readSavedFilterUi = (): FilterUiState | null => {
  try {
    const raw = sessionStorage.getItem(FILTER_UI_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as FilterUiState) : null;
  } catch {
    return null;
  }
};

const persistOpenFilterUi = (ui: FilterUiState) => {
  if (!ui.showFilters && !ui.filtersDocked) return;
  sessionStorage.setItem(FILTER_UI_STORAGE_KEY, JSON.stringify(ui));
};

interface TableState {
  rowsPerPage: number;
  currentPage: number;
  selectedCategory1: string;
  selectedCategory2: string;
  selectedCategory3: string;
  dateFrom: string;
  dateTo: string;
  amountMin: string;
  amountMax: string;
  vendorSearch: string;
  submittedBy: string;
  creditsOnly: boolean;
  selectedReviewStatuses: ReviewStatusDotState[];
  sortField: string;
  sortDirection: 'asc' | 'desc';
  showFilters: boolean;
  filtersDocked: boolean;
  filterPanelPos: { left: number; top: number } | null;
  filterPanelSize: { width: number; height: number | null };
}

const toTableExpense = (item: any): Expense => {
  const netAmount = roundMoney(parseFloat(item.net_amount?.toString() || '0'));
  const vatAmount = roundMoney(parseFloat(item.vat_amount?.toString() || '0'));
  const rawTotal = item.total_amount;
  const parsedTotal =
    rawTotal === undefined || rawTotal === null || rawTotal === ''
      ? undefined
      : parseFloat(rawTotal.toString());
  const totalAmount =
    parsedTotal !== undefined && Number.isFinite(parsedTotal)
      ? roundMoney(parsedTotal)
      : undefined;
  const vatPercentage =
    item.vat_percentage !== undefined && item.vat_percentage !== null
      ? parseFloat(item.vat_percentage?.toString() || '0')
      : 100;
  const vatPercentageFromTree = Boolean(item.vat_percentage_from_tree);
  const vatPercentageCategoryLevel =
    item.vat_percentage_category_level === undefined ||
    item.vat_percentage_category_level === null
      ? null
      : Number(item.vat_percentage_category_level);

  const noVat = Boolean(item.no_vat);
  const expenseId = item.id.toString();
  const manualEditFlags = getManualEditsForExpense(expenseId);

  return {
    id: expenseId,
    vendorName: item.vendor_name || '',
    amount: noVat ? (totalAmount ?? netAmount) : netAmount,
    totalAmount,
    category1: item.category_1 || '',
    category2: item.category_2 || '',
    category3: item.category_3 || '',
    projectTag: item.project_tag || '',
    documentDate: item.receipt_date || '',
    submittedBy: item.submitted_by || '',
    updatedBy: item.updated_by || '',
    invoiceNumber: item.invoice_number || '',
    documentNumber: item.invoice_number || '',
    notes: item.notes || '',
    imageUrl: item.s3_image_key || '',
    creationTimestamp: item.creation_timestamp || '',
    manuallyEdited: Object.keys(manualEditFlags).length > 0 ? manualEditFlags : undefined,
    vatEligible: vatPercentageFromTree ? vatPercentage > 0 : true,
    vatPercentage,
    vatAmount: noVat ? 0 : vatAmount,
    noVat,
    isCredit: isCreditDocumentType(item.document_type),
    amountNeedsReview: amountNeedsReviewFromLevel(item.confidence_level),
    currency: item.currency || 'ILS',
    originalNetAmount: optionalNumber(item.original_net_amount),
    originalVatAmount: optionalNumber(item.original_vat_amount),
    originalTotalAmount: optionalNumber(item.original_total_amount),
    fxRate: optionalNumber(item.fx_rate),
    fxRateDate: item.fx_rate_date ? String(item.fx_rate_date).slice(0, 10) : '',
    fxRateOverridden: Boolean(item.fx_rate_overridden),
    originalReceiptId:
      item.original_receipt_id === undefined || item.original_receipt_id === null
        ? null
        : Number(item.original_receipt_id),
    vatPercentageCategoryLevel,
    vatPercentageFromTree,
    originalData: item,
  };
};

const ExpenseTable: React.FC = () => {
  const navigate = useNavigate();
  const { rivhitDocsEnabled, reload: reloadCpa } = useCpa();
  const { state, dispatch } = useApp();
  const { setLoading: setGlobalLoading, showError, showSuccess, showConfirmation, showWarning } = useToast();
  const { flatCategories } = state;
  const [selectedReviewStatuses, setSelectedReviewStatuses] = useState<
    Set<ReviewStatusDotState>
  >(new Set());
  const [recurrenceAlert, setRecurrenceAlert] = useState<{
    seriesId: string;
    vendorName: string;
    endDate: string;
  } | null>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const tableBodyRef = useRef<HTMLDivElement>(null);
  const hasRestoredStateRef = useRef(false);
  const isRestoringStateRef = useRef(false);
  const restoredPageRef = useRef<number | null>(null);
  const suppressOutsideCloseUntilRef = useRef(0);
  
  // user is no longer needed since tenant_id comes from JWT automatically
  const [showFilters, setShowFilters] = useState(false);
  const [filtersDocked, setFiltersDocked] = useState(false);
  const [filterPanelPos, setFilterPanelPos] = useState<{ left: number; top: number } | null>(null);
  const [filterPanelSize, setFilterPanelSize] = useState<{ width: number; height: number | null }>({
    width: 720,
    height: null,
  });
  const [dockPreviewVisible, setDockPreviewVisible] = useState(false);
  const [isDraggingFilter, setIsDraggingFilter] = useState(false);
  const filterPanelRef = useRef<HTMLDivElement>(null);
  const filterButtonWrapRef = useRef<HTMLDivElement>(null);
  const filterDockPanelRef = useRef<HTMLElement>(null);
  const [dockPanelLayout, setDockPanelLayout] = useState<{ top: number; maxHeight: number } | null>(null);
  const filterDragRef = useRef<{
    startX: number;
    startY: number;
    origLeft: number;
    origTop: number;
  } | null>(null);
  const filterResizeRef = useRef<{
    startX: number;
    startY: number;
    origWidth: number;
    origHeight: number;
  } | null>(null);
  const FILTER_DOCK_ZONE_PX = 220;
  const FILTER_DOCK_WIDTH_PX = 320;
  const FILTER_DOCK_LEFT_PX = 12;
  const FILTER_FLOAT_MIN_WIDTH = 480;
  const FILTER_FLOAT_MIN_HEIGHT = 160;
  const FILTER_FLOAT_DEFAULT_WIDTH = 720;
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadProgress, setLoadProgress] = useState<{ loaded: number; total: number } | null>(null);

  useEffect(() => {
    setGlobalLoading(loading, 'expenseTable');
    return () => setGlobalLoading(false, 'expenseTable');
  }, [loading, setGlobalLoading]);

  // Filter states
  const [selectedCategory1, setSelectedCategory1] = useState('');
  const [selectedCategory2, setSelectedCategory2] = useState('');
  const [selectedCategory3, setSelectedCategory3] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');
  const [vendorSearch, setVendorSearch] = useState('');
  const [submittedBy, setSubmittedBy] = useState('');
  const [creditsOnly, setCreditsOnly] = useState(false);

  // Category dropdown options
  const [availableCategories2, setAvailableCategories2] = useState<FlatCategory[]>([]);
  const [availableCategories3, setAvailableCategories3] = useState<FlatCategory[]>([]);

  // Sorting state - default to creation date descending (newest first)
  const [sortField, setSortField] = useState<string>('creationTimestamp');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const resolveFilterUiForSave = (): FilterUiState => {
    const savedOpenUi = readSavedFilterUi();
    const panelOpen = showFilters || filtersDocked;
    const resolvedShowFilters = panelOpen || Boolean(savedOpenUi?.showFilters);
    const resolvedDocked = filtersDocked || Boolean(savedOpenUi?.filtersDocked);
    return {
      showFilters: resolvedShowFilters,
      filtersDocked: resolvedDocked,
      filterPanelPos: filterPanelPos ?? savedOpenUi?.filterPanelPos ?? null,
      filterPanelSize: filterPanelSize ?? savedOpenUi?.filterPanelSize ?? {
        width: FILTER_FLOAT_DEFAULT_WIDTH,
        height: null,
      },
    };
  };

  // Save table state to sessionStorage
  const saveTableState = () => {
    const filterUi = resolveFilterUiForSave();
    const tableState: TableState = {
      rowsPerPage,
      currentPage,
      selectedCategory1,
      selectedCategory2,
      selectedCategory3,
      dateFrom,
      dateTo,
      amountMin,
      amountMax,
      vendorSearch,
      submittedBy,
      creditsOnly,
      selectedReviewStatuses: Array.from(selectedReviewStatuses),
      sortField,
      sortDirection,
      ...filterUi,
    };
    sessionStorage.setItem(TABLE_STATE_STORAGE_KEY, JSON.stringify(tableState));
    persistOpenFilterUi(filterUi);
    
    // Save scroll position
    if (tableBodyRef.current) {
      const scrollPosition = tableBodyRef.current.scrollTop;
      sessionStorage.setItem(TABLE_SCROLL_STORAGE_KEY, scrollPosition.toString());
    }
  };

  // Restore table state from sessionStorage
  const restoreTableState = () => {
    if (hasRestoredStateRef.current) return; // Only restore once
    
      try {
        const savedState = sessionStorage.getItem(TABLE_STATE_STORAGE_KEY);
        if (savedState) {
          const tableState: TableState = JSON.parse(savedState);
          
          // Store the target page number to restore after all state updates
          restoredPageRef.current = tableState.currentPage || 1;
          
          // Mark that we're currently restoring state to prevent filter reset
          isRestoringStateRef.current = true;
          
          // Restore all state values EXCEPT currentPage (we'll set it after)
          const savedFilterUi = readSavedFilterUi();
          const shouldShowFilters = Boolean(tableState.showFilters || savedFilterUi?.showFilters);
          const shouldDock = Boolean(tableState.filtersDocked || savedFilterUi?.filtersDocked);
          const keepFilters = shouldShowFilters || shouldDock;

          setRowsPerPage(tableState.rowsPerPage || 25);
          setSelectedCategory1(keepFilters ? tableState.selectedCategory1 || '' : '');
          setSelectedCategory2(keepFilters ? tableState.selectedCategory2 || '' : '');
          setSelectedCategory3(keepFilters ? tableState.selectedCategory3 || '' : '');
          setDateFrom(keepFilters ? tableState.dateFrom || '' : '');
          setDateTo(keepFilters ? tableState.dateTo || '' : '');
          setAmountMin(keepFilters ? tableState.amountMin || '' : '');
          setAmountMax(keepFilters ? tableState.amountMax || '' : '');
          setVendorSearch(keepFilters ? tableState.vendorSearch || '' : '');
          setSubmittedBy(keepFilters ? tableState.submittedBy || '' : '');
          setCreditsOnly(keepFilters ? Boolean(tableState.creditsOnly) : false);
          setSelectedReviewStatuses(
            new Set(
              keepFilters && Array.isArray(tableState.selectedReviewStatuses)
                ? tableState.selectedReviewStatuses
                : []
            )
          );
          setSortField(tableState.sortField || 'creationTimestamp');
          setSortDirection(tableState.sortDirection || 'desc');

          setShowFilters(shouldShowFilters);
          setFiltersDocked(shouldDock);
          setFilterPanelPos(
            tableState.filterPanelPos ?? savedFilterUi?.filterPanelPos ?? null
          );
          setFilterPanelSize(
            tableState.filterPanelSize ??
              savedFilterUi?.filterPanelSize ?? {
                width: FILTER_FLOAT_DEFAULT_WIDTH,
                height: null,
              }
          );

          if (shouldShowFilters) {
            suppressOutsideCloseUntilRef.current = Date.now() + 500;
          }
          
          // Keep saved state in sessionStorage so React Strict Mode remount
          // (and return after delete) still restores the same filters/sort.
          
          // Mark restoration as complete after React processes the state updates
          // Then restore the page number after the filter reset useEffect has run
          setTimeout(() => {
            isRestoringStateRef.current = false;
            hasRestoredStateRef.current = true;
            // Now set the page number after restoration is complete and filter reset has run
            if (restoredPageRef.current !== null) {
              // Use another setTimeout to ensure this runs after the filter reset useEffect
              setTimeout(() => {
                setCurrentPage(restoredPageRef.current!);
                restoredPageRef.current = null;
              }, 50);
            }
          }, 100);
        } else {
          // No saved state, mark as restored to allow normal behavior
          hasRestoredStateRef.current = true;
        }
      } catch (error) {
        console.error('Error restoring table state:', error);
        isRestoringStateRef.current = false;
        hasRestoredStateRef.current = true; // Mark as restored even on error
      }
  };

  // Restore scroll position after data loads
  const restoreScrollPosition = () => {
    try {
      const savedScroll = sessionStorage.getItem(TABLE_SCROLL_STORAGE_KEY);
      if (savedScroll && tableBodyRef.current) {
        const scrollPosition = parseInt(savedScroll, 10);
        // Use setTimeout to ensure DOM is fully rendered
        setTimeout(() => {
          if (tableBodyRef.current) {
            tableBodyRef.current.scrollTop = scrollPosition;
            // Clear scroll position after restoring to avoid restoring on next visit
            sessionStorage.removeItem(TABLE_SCROLL_STORAGE_KEY);
          }
        }, 100);
      }
    } catch (error) {
      console.error('Error restoring scroll position:', error);
    }
  };
  // Show the first page immediately, then keep loading the rest (API max page_size is 100).
  useEffect(() => {
    let cancelled = false;

    const publishExpenses = (items: any[]) => {
      const transformedExpenses = items
        .filter((item: any) => item.status !== 'deleted')
        .map((item: any) => toTableExpense(item));
      setExpenses(transformedExpenses);
      dispatch({ type: 'SET_EXPENSES', payload: transformedExpenses });
    };

    const fetchAllExpenses = async () => {
      try {
        setLoading(true);
        setLoadingMore(false);
        setLoadProgress({ loaded: 0, total: 0 });

        const pageSize = 100;
        const allItems: any[] = [];
        let page = 1;
        let total = Infinity;

        while (allItems.length < total) {
          if (cancelled) return;
          const response = await api.expenses.getAll({
            page,
            limit: pageSize,
            sortBy: 'id',
            sortOrder: 'desc',
          });
          if (cancelled) return;
          if (!response.success || !response.data) {
            showError(
              page === 1
                ? 'שגיאה בטעינת ההוצאות: ' + (response.error || 'שגיאה לא ידועה')
                : 'שגיאה בטעינת שאר ההוצאות',
              {
                action: 'Load expenses list',
                error: response.error,
              },
            );
            break;
          }

          const backendData = response.data as {
            items: any[];
            total: number;
            page: number;
            page_size: number;
            recurrence_alerts?: Array<{ series_id: string; vendor_name: string; end_date: string }>;
          };
          const batch = backendData.items || [];
          allItems.push(...batch);
          total = Number(backendData.total ?? allItems.length);
          setLoadProgress({
            loaded: allItems.length,
            total: Number.isFinite(total) && total > 0 ? total : 0,
          });
          if (allItems.length > 0 || total === 0) {
            publishExpenses(allItems);
          }
          const more = shouldLoadAnotherExpensePage(allItems.length, total, batch.length, pageSize);
          if (page === 1) {
            const recurrenceAlerts = backendData.recurrence_alerts;
            if (
              !recurrenceReminderHandledThisLoad &&
              Array.isArray(recurrenceAlerts) &&
              recurrenceAlerts.length > 0
            ) {
              const tenantId = String(state.auth.user?.tenant_id || state.auth.tenant?.id || 'default');
              const nextAlert = recurrenceAlerts.find((alert) =>
                canShowRecurrenceReminder(tenantId, alert.series_id)
              );
              if (nextAlert) {
                recurrenceReminderHandledThisLoad = true;
                setRecurrenceAlert({
                  seriesId: nextAlert.series_id,
                  vendorName: nextAlert.vendor_name || '',
                  endDate: nextAlert.end_date,
                });
              }
            }
            setLoading(false);
            setLoadingMore(more);
          }
          if (!more) break;
          page += 1;
        }
      } catch (err) {
        console.error('Error fetching expenses:', err);
        showError('שגיאה בטעינת ההוצאות', {
          action: 'Load expenses list',
          error: err,
        });
      } finally {
        if (!cancelled) {
          setLoading(false);
          setLoadingMore(false);
          setLoadProgress(null);
        }
      }
    };

    // Restore table state before fetching data
    restoreTableState();

    fetchAllExpenses();
    // Clear edit-reload marker if present (fetch above already loads fresh data)
    sessionStorage.removeItem(RELOAD_AFTER_EDIT_KEY);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Restore scroll position after expenses are loaded and rendered
  useEffect(() => {
    if (!loading && expenses.length > 0) {
      restoreScrollPosition();
    }
  }, [loading, expenses.length]);


  // Reset to page 1 when filters change (but not when restoring state)
  useEffect(() => {
    // Only reset to page 1 if we're NOT currently restoring state
    // This prevents the page reset during state restoration
    if (!isRestoringStateRef.current) {
      setCurrentPage(1);
    }
  }, [selectedCategory1, selectedCategory2, selectedCategory3, dateFrom, dateTo, amountMin, amountMax, vendorSearch, submittedBy, creditsOnly, selectedReviewStatuses]);

  // Update available categories based on selections (similar to editor)
  // Wait for the category tree before cascading clears — otherwise restore from
  // editor briefly has filters set while flatCategories is still null and wipes L2/L3.
  useEffect(() => {
    if (!flatCategories) return;

    if (selectedCategory1) {
      const category1 = findCategoryByName(flatCategories.categories1, selectedCategory1);
      if (category1) {
        const subcats2 = getSubcategories(flatCategories.allCategories, category1.id, 2);
        setAvailableCategories2(subcats2);
        if (selectedCategory2 && !subcats2.find(c => c.name === selectedCategory2)) {
          setSelectedCategory2('');
          setSelectedCategory3('');
        }
      } else {
        setAvailableCategories2([]);
      }
    } else {
      setAvailableCategories2([]);
      if (selectedCategory2) {
        setSelectedCategory2('');
        setSelectedCategory3('');
      }
    }
  }, [selectedCategory1, selectedCategory2, flatCategories]);

  useEffect(() => {
    if (!flatCategories) return;

    if (selectedCategory1 && selectedCategory2) {
      const category2 = findCategoryByName(availableCategories2, selectedCategory2);
      if (category2) {
        const subcats3 = getSubcategories(flatCategories.allCategories, category2.id, 3);
        setAvailableCategories3(subcats3);
        if (selectedCategory3 && !subcats3.find(c => c.name === selectedCategory3)) {
          setSelectedCategory3('');
        }
      } else if (availableCategories2.length > 0) {
        setAvailableCategories3([]);
      }
    } else {
      setAvailableCategories3([]);
      if (selectedCategory3) {
        setSelectedCategory3('');
      }
    }
  }, [selectedCategory1, selectedCategory2, selectedCategory3, availableCategories2, flatCategories]);

  // Get unique values for dropdowns from actual expense data
  const uniqueVendors = useMemo(() => {
    const vendors = new Set<string>();
    expenses.forEach(expense => {
      if (expense.vendorName) vendors.add(expense.vendorName);
    });
    return Array.from(vendors).sort();
  }, [expenses]);

  const uniqueSubmittedBy = useMemo(() => {
    const users = new Set<string>();
    expenses.forEach(expense => {
      if (expense.submittedBy) users.add(expense.submittedBy);
    });
    return Array.from(users).sort();
  }, [expenses]);

  // Apply filters to expenses
  const filteredExpenses = useMemo(() => {
    return expenses.filter(expense => {
      // Category filters
      if (selectedCategory1 && expense.category1 !== selectedCategory1) return false;
      if (selectedCategory2 && expense.category2 !== selectedCategory2) return false;
      if (selectedCategory3 && expense.category3 !== selectedCategory3) return false;

      // Date filters
      if (dateFrom) {
        const expenseDate = new Date(expense.documentDate);
        const fromDate = new Date(dateFrom);
        if (expenseDate < fromDate) return false;
      }
      if (dateTo) {
        const expenseDate = new Date(expense.documentDate);
        const toDate = new Date(dateTo);
        toDate.setHours(23, 59, 59, 999); // Include entire end date
        if (expenseDate > toDate) return false;
      }

      if (!matchesCreditFilter(expense.isCredit, creditsOnly)) return false;

      // Amount filters
      const compareAmount = amountForFilter(expense.amount, creditsOnly);
      if (amountMin) {
        const min = parseFloat(amountMin);
        if (isNaN(min) || compareAmount < min) return false;
      }
      if (amountMax) {
        const max = parseFloat(amountMax);
        if (isNaN(max) || compareAmount > max) return false;
      }

      // Vendor filter
      if (vendorSearch && expense.vendorName !== vendorSearch) return false;

      // Submitted by filter
      if (submittedBy && expense.submittedBy !== submittedBy) return false;

      // Review status dot filter
      if (
        !expenseMatchesReviewStatusFilterSet(
          expense,
          isExpenseReviewed(expense.id),
          selectedReviewStatuses
        )
      ) {
        return false;
      }

      return true;
    });
  }, [expenses, selectedCategory1, selectedCategory2, selectedCategory3, dateFrom, dateTo, amountMin, amountMax, vendorSearch, submittedBy, creditsOnly, selectedReviewStatuses]);

  // Calculate summary statistics from FILTERED data
  const summaryStats = useMemo(() => {
    const totalLines = filteredExpenses.length;
    const totalNet = filteredExpenses.reduce((sum, expense) => sum + (expense.amount || 0), 0);
    const totalVAT = filteredExpenses.reduce((sum, expense) => sum + (expense.vatAmount || 0), 0);
    const totalWithVAT = filteredExpenses.reduce((sum, expense) => {
      if (expense.totalAmount != null && !Number.isNaN(Number(expense.totalAmount))) {
        return sum + Number(expense.totalAmount);
      }
      return sum + (expense.amount || 0) + (expense.vatAmount || 0);
    }, 0);

    return {
      totalLines,
      totalNet,
      totalVAT,
      totalWithVAT,
    };
  }, [filteredExpenses]);

  // Instant client-side sorting for FILTERED data
  const sortedExpenses = useMemo(() => {
    if (!sortField) return filteredExpenses;

    const statusRank = (expense: Expense): number => {
      if (getCriticalMissingFields(expense).length > 0) return 0; // red
      if (isExpenseReviewed(expense.id)) return 2; // green
      if (getSoftMissingFields(expense).length > 0) return 1; // pink
      return 3;
    };

    return [...filteredExpenses].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      if (sortField === 'invoiceNumber') {
        aValue = getInvoiceNumber(a).toLowerCase();
        bValue = getInvoiceNumber(b).toLowerCase();
      } else if (sortField === 'reviewStatus') {
        aValue = statusRank(a);
        bValue = statusRank(b);
      } else {
        aValue = a[sortField as keyof Expense];
        bValue = b[sortField as keyof Expense];

        if (sortField === 'amount' || sortField === 'vatAmount' || sortField === 'totalAmount' || sortField === 'id') {
          aValue = Number(aValue) || 0;
          bValue = Number(bValue) || 0;
        } else if (sortField === 'documentDate' || sortField === 'creationTimestamp') {
          aValue = new Date(aValue || 0).getTime();
          bValue = new Date(bValue || 0).getTime();
        } else {
          aValue = String(aValue || '').toLowerCase();
          bValue = String(bValue || '').toLowerCase();
        }
      }

      if (aValue < bValue) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredExpenses, sortField, sortDirection]);

  // Calculate pagination based on FILTERED and SORTED data
  const totalPages = Math.max(1, Math.ceil(sortedExpenses.length / rowsPerPage) || 1);
  const safePage = Math.min(Math.max(currentPage, 1), totalPages);
  const startIndex = (safePage - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, sortedExpenses.length);

  // If restored/saved page is past the filtered results (common after delete), clamp it
  useEffect(() => {
    if (loading) return;
    if (currentPage !== safePage) {
      setCurrentPage(safePage);
    }
  }, [loading, currentPage, safePage]);


  const handleEditExpense = (expense: Expense) => {
    // Save table state and the filtered/sorted row order for editor prev/next navigation
    saveTableState();
    saveTableNavList(sortedExpenses.map((row) => row.id));
    dispatch({ type: 'SET_SELECTED_EXPENSE', payload: expense });
    navigate(`/editor?id=${expense.id}`);
  };

  const handleAddNewExpense = () => {
    navigate('/upload');
  };

  const handleExportToCpa = async () => {
    if (!rivhitDocsEnabled) return;
    setGlobalLoading(true, 'cpa-export');
    try {
      const response = await api.expenses.cpaExport({});
      const data = (response.data || {}) as {
        sent?: boolean;
        included?: number;
        emailed?: boolean;
        error?: string;
        message?: string;
        sentTo?: string;
      };
      if (!response.success) {
        showError(response.error || 'השליחה לרו״ח נכשלה');
        return;
      }
      if (data.sent && data.emailed) {
        showSuccess(`נשלחו ${data.included || 0} הוצאות אל ${data.sentTo || 'RivhitDocs'}`);
        reloadCpa();
        return;
      }
      showError(data.error || data.message || 'אין הוצאות לשליחה');
    } finally {
      setGlobalLoading(false, 'cpa-export');
    }
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const clearFilterValues = () => {
    setSelectedCategory1('');
    setSelectedCategory2('');
    setSelectedCategory3('');
    setDateFrom('');
    setDateTo('');
    setAmountMin('');
    setAmountMax('');
    setVendorSearch('');
    setSubmittedBy('');
    setCreditsOnly(false);
    setSelectedReviewStatuses(new Set());
    setAvailableCategories2([]);
    setAvailableCategories3([]);
    setCurrentPage(1);
  };

  // Remember open filter UI while the panel is visible (survives row-click race)
  useEffect(() => {
    if (isRestoringStateRef.current) return;
    persistOpenFilterUi({
      showFilters,
      filtersDocked,
      filterPanelPos,
      filterPanelSize,
    });
  }, [showFilters, filtersDocked, filterPanelPos, filterPanelSize]);

  const closeFilterPanel = () => {
    clearFilterValues();
    setShowFilters(false);
    setFiltersDocked(false);
    setFilterPanelPos(null);
    setDockPreviewVisible(false);
    setIsDraggingFilter(false);
    filterResizeRef.current = null;
    sessionStorage.removeItem(FILTER_UI_STORAGE_KEY);
    try {
      const raw = sessionStorage.getItem(TABLE_STATE_STORAGE_KEY);
      if (raw) {
        const saved = JSON.parse(raw) as TableState;
        sessionStorage.setItem(
          TABLE_STATE_STORAGE_KEY,
          JSON.stringify({
            ...saved,
            selectedCategory1: '',
            selectedCategory2: '',
            selectedCategory3: '',
            dateFrom: '',
            dateTo: '',
            amountMin: '',
            amountMax: '',
            vendorSearch: '',
            submittedBy: '',
            creditsOnly: false,
            selectedReviewStatuses: [],
            showFilters: false,
            filtersDocked: false,
            filterPanelPos: null,
            currentPage: 1,
          })
        );
      }
    } catch {
      // ignore storage errors
    }
  };

  // Click outside the floating filter panel closes it and clears filters.
  // Use "click" (not mousedown) and ignore table clicks so opening a row does not
  // close the panel before saveTableState runs.
  useEffect(() => {
    if (!showFilters || filtersDocked) return;

    const onOutsideClick = (event: MouseEvent) => {
      if (Date.now() < suppressOutsideCloseUntilRef.current) return;

      const target = event.target as Node;
      if (filterPanelRef.current?.contains(target)) return;
      if (filterButtonWrapRef.current?.contains(target)) return;
      if (tableContainerRef.current?.contains(target)) return;
      closeFilterPanel();
    };

    document.addEventListener('click', onOutsideClick, true);
    return () => document.removeEventListener('click', onOutsideClick, true);
  }, [showFilters, filtersDocked]);

  const handleFilterButtonClick = () => {
    if (showFilters || filtersDocked) {
      closeFilterPanel();
      return;
    }
    const wrap = filterButtonWrapRef.current;
    const width = Math.min(
      filterPanelSize.width,
      typeof window !== 'undefined' ? window.innerWidth - 24 : filterPanelSize.width
    );
    if (wrap) {
      const rect = wrap.getBoundingClientRect();
      setFilterPanelPos({
        left: Math.max(12, Math.min(rect.right - width, window.innerWidth - width - 12)),
        top: Math.min(rect.bottom + 8, window.innerHeight - FILTER_FLOAT_MIN_HEIGHT - 12),
      });
    } else {
      setFilterPanelPos({ left: 24, top: 96 });
    }
    setShowFilters(true);
  };

  const handleFilterDragStart = (e: React.MouseEvent) => {
    e.preventDefault();
    filterResizeRef.current = null;

    // Undock: drag away from docked position → restore full table columns
    if (filtersDocked) {
      const dockEl = filterDockPanelRef.current;
      if (!dockEl) return;
      const rect = dockEl.getBoundingClientRect();
      const left = rect.left;
      const top = rect.top;
      setFiltersDocked(false);
      setFilterPanelPos({ left, top });
      setFilterPanelSize({
        width: FILTER_FLOAT_DEFAULT_WIDTH,
        height: null,
      });
      filterDragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        origLeft: left,
        origTop: top,
      };
      setIsDraggingFilter(true);
      setDockPreviewVisible(e.clientX < FILTER_DOCK_ZONE_PX);
      return;
    }

    const panel = filterPanelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    const left = filterPanelPos?.left ?? rect.left;
    const top = filterPanelPos?.top ?? rect.top;
    if (!filterPanelPos) {
      setFilterPanelPos({ left, top });
    }
    filterDragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      origLeft: left,
      origTop: top,
    };
    setIsDraggingFilter(true);
    setDockPreviewVisible(e.clientX < FILTER_DOCK_ZONE_PX);
  };

  const handleFilterResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    filterDragRef.current = null;
    const panel = filterPanelRef.current;
    const measuredHeight =
      filterPanelSize.height ??
      (panel ? Math.round(panel.getBoundingClientRect().height) : FILTER_FLOAT_MIN_HEIGHT);
    filterResizeRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      origWidth: filterPanelSize.width,
      origHeight: measuredHeight,
    };
  };

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (filterResizeRef.current) {
        const dx = e.clientX - filterResizeRef.current.startX;
        const dy = e.clientY - filterResizeRef.current.startY;
        const maxW = Math.max(FILTER_FLOAT_MIN_WIDTH, window.innerWidth - 24);
        const maxH = Math.max(FILTER_FLOAT_MIN_HEIGHT, window.innerHeight - 24);
        setFilterPanelSize({
          width: Math.min(maxW, Math.max(FILTER_FLOAT_MIN_WIDTH, filterResizeRef.current.origWidth + dx)),
          height: Math.min(maxH, Math.max(FILTER_FLOAT_MIN_HEIGHT, filterResizeRef.current.origHeight + dy)),
        });
        return;
      }
      if (!filterDragRef.current) return;
      const dx = e.clientX - filterDragRef.current.startX;
      const dy = e.clientY - filterDragRef.current.startY;
      setFilterPanelPos({
        left: Math.max(0, filterDragRef.current.origLeft + dx),
        top: Math.max(0, filterDragRef.current.origTop + dy),
      });
      // Windows-style snap: highlight dock zone when near left edge
      setDockPreviewVisible(e.clientX < FILTER_DOCK_ZONE_PX);
    };
    const onUp = (e: MouseEvent) => {
      if (filterResizeRef.current) {
        filterResizeRef.current = null;
        return;
      }
      if (!filterDragRef.current) return;
      filterDragRef.current = null;
      setIsDraggingFilter(false);
      const shouldDock = e.clientX < FILTER_DOCK_ZONE_PX;
      setDockPreviewVisible(false);
      if (shouldDock) {
        setFiltersDocked(true);
        setFilterPanelPos(null);
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  // If filters reopen floating without a position (e.g. session restore), place under the icon
  useEffect(() => {
    if (!showFilters || filtersDocked || filterPanelPos) return;
    const wrap = filterButtonWrapRef.current;
    const width = Math.min(filterPanelSize.width, window.innerWidth - 24);
    if (!wrap) {
      setFilterPanelPos({ left: 24, top: 96 });
      return;
    }
    const rect = wrap.getBoundingClientRect();
    setFilterPanelPos({
      left: Math.max(12, Math.min(rect.right - width, window.innerWidth - width - 12)),
      top: Math.min(rect.bottom + 8, window.innerHeight - FILTER_FLOAT_MIN_HEIGHT - 12),
    });
  }, [showFilters, filtersDocked, filterPanelPos, filterPanelSize.width]);

  const handleExportToCSV = () => {
    if (loadingMore) {
      showWarning('הרשימה עדיין נטענת. הייצוא זמין כשהטעינה מסתיימת.');
      return;
    }
    // Use filtered and sorted expenses for export
    const dataToExport = sortedExpenses;

    // Helper function to escape quotes in CSV fields
    const escapeCSVField = (value: string | undefined | null): string => {
      return (value || '').replace(/"/g, '""');
    };

    // Define CSV headers (matching table columns)
    const headers = [
      FIELD_LABELS.serialNumber,
      FIELD_LABELS.category1,
      FIELD_LABELS.category2,
      FIELD_LABELS.category3,
      FIELD_LABELS.amountShort,
      FIELD_LABELS.vatAmount,
      FIELD_LABELS.vendorName,
      FIELD_LABELS.documentDate,
      FIELD_LABELS.submittedBy,
      FIELD_LABELS.updatedBy,
      FIELD_LABELS.invoiceNumber,
      FIELD_LABELS.notes,
    ];

    // Convert expenses to CSV rows
    const csvRows = [
      headers.join(','), // Header row
      ...dataToExport.map((expense, index) => {
        const row = [
          (index + 1).toString(), // Serial number
          `"${escapeCSVField(expense.category1)}"`,
          `"${escapeCSVField(expense.category2)}"`,
          `"${escapeCSVField(expense.category3)}"`,
          `"${formatMoneyPlain(expense.amount)}"`,
          `"${expense.noVat ? NO_VAT_DISPLAY : formatMoneyPlain(expense.vatAmount || 0)}"`,
          `"${escapeCSVField(expense.vendorName)}"`,
          `"${escapeCSVField(formatMetricaDate(expense.documentDate))}"`,
          `"${escapeCSVField(expense.submittedBy)}"`,
          `"${escapeCSVField(expense.updatedBy)}"`,
          `"${escapeCSVField(getInvoiceNumber(expense))}"`,
          `"${escapeCSVField(expense.notes)}"`
        ];
        return row.join(',');
      })
    ];

    // Create CSV content
    const csvContent = csvRows.join('\n');

    // Create BOM for UTF-8 to ensure Hebrew displays correctly in Excel
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });

    // Create download link
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    
    // Generate filename with current date
    const date = new Date().toISOString().split('T')[0];
    link.setAttribute('download', `expenses_${date}.csv`);
    
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatAmount = (amount: number) => formatMoney(amount);

  // Sorting: click header to toggle asc/desc; active column shows a small arrow
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      // Dates default to newest/latest first when newly selected
      const dateFields = ['creationTimestamp', 'documentDate'];
      setSortDirection(dateFields.includes(field) ? 'desc' : 'asc');
    }
  };

  const sortAria = (field: string): 'ascending' | 'descending' | 'none' => {
    if (sortField !== field) return 'none';
    return sortDirection === 'asc' ? 'ascending' : 'descending';
  };

  const renderSortableHeader = (
    field: string,
    label: React.ReactNode,
    options?: { extraClassName?: string; ariaLabel?: string }
  ) => (
    <th
      className={`${styles.headerCell} ${styles.sortableHeader} ${options?.extraClassName || ''}`.trim()}
      onClick={() => handleSort(field)}
      aria-sort={sortAria(field)}
      aria-label={options?.ariaLabel || (typeof label === 'string' ? label.replace(/\n/g, ' ') : undefined)}
    >
      <span className={styles.headerSortContent}>
        <span className={styles.headerLabel}>{label}</span>
        {sortField === field && (
          <span className={styles.sortArrow} aria-hidden="true">
            {sortDirection === 'asc' ? '▲' : '▼'}
          </span>
        )}
      </span>
    </th>
  );

  // Client-side pagination
  const paginatedExpenses = useMemo(() => {
    return sortedExpenses.slice(startIndex, endIndex);
  }, [sortedExpenses, startIndex, endIndex]);

  const compactColumns = filtersDocked;
  const filterPanelFloating = showFilters && !filtersDocked;
  const filterPanelDocked = showFilters && filtersDocked;

  // Center docked filter panel vertically against the table (header → last visible row)
  useLayoutEffect(() => {
    if (!filterPanelDocked) {
      setDockPanelLayout(null);
      return;
    }

    const updateDockLayout = () => {
      const tableEl = tableContainerRef.current;
      const panelEl = filterDockPanelRef.current;
      if (!tableEl) return;

      const tableRect = tableEl.getBoundingClientRect();
      if (tableRect.height <= 0) return;

      const maxHeight = Math.max(160, tableRect.height);
      const panelHeight = panelEl ? panelEl.getBoundingClientRect().height : 0;
      const effectiveHeight = panelHeight > 0 ? Math.min(panelHeight, maxHeight) : maxHeight;
      const top = tableRect.top + (tableRect.height - effectiveHeight) / 2;

      setDockPanelLayout((prev) => {
        if (prev && Math.abs(prev.top - top) < 0.5 && Math.abs(prev.maxHeight - maxHeight) < 0.5) {
          return prev;
        }
        return { top, maxHeight };
      });
    };

    updateDockLayout();
    const raf = window.requestAnimationFrame(updateDockLayout);

    const tableEl = tableContainerRef.current;
    const panelEl = filterDockPanelRef.current;
    const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateDockLayout) : null;
    if (resizeObserver && tableEl) resizeObserver.observe(tableEl);
    if (resizeObserver && panelEl) resizeObserver.observe(panelEl);

    window.addEventListener('resize', updateDockLayout);
    window.addEventListener('scroll', updateDockLayout, true);

    return () => {
      window.cancelAnimationFrame(raf);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateDockLayout);
      window.removeEventListener('scroll', updateDockLayout, true);
    };
  }, [filterPanelDocked, summaryStats.totalLines, rowsPerPage, paginatedExpenses.length]);

  const filterOverflowNote =
    summaryStats.totalLines > rowsPerPage ? (
      <div className={styles.filterDockOverflowNote}>
        <div className={styles.noteIcon} aria-hidden>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2L2 22h20L12 2z" fill="#ffc107" />
          </svg>
        </div>
        <span className={styles.noteText}>
          נתוני הסיכום המוצגים לוקחים בחשבון את כל התוצאות על פי המסננים למרות שהדוח עשוי להכיל יותר שורות מאלה המוצגות על המסך.
        </span>
      </div>
    ) : null;

  const filterPanelBodyDocked = (
    <div className={`${styles.filterBar} ${styles.filterBarDocked}`}>
      <div className={styles.filterDockFieldGrid}>
        <SearchableSelect
          value={selectedCategory1}
          onChange={(next) => {
            setSelectedCategory1(next);
            setSelectedCategory2('');
            setSelectedCategory3('');
          }}
          options={optionsFromNames(flatCategories?.categories1 || [])}
          ariaLabel={FIELD_LABELS.category1Short}
          emptyOption={{ value: '', label: 'הכל' }}
          compact
        />
        <div className={`${styles.filterLabel} ${styles.filterLabelL1}`}>{FIELD_LABELS.category1Short}</div>

        <SearchableSelect
          value={selectedCategory2}
          onChange={(next) => {
            setSelectedCategory2(next);
            setSelectedCategory3('');
          }}
          options={optionsFromNames(availableCategories2)}
          ariaLabel={FIELD_LABELS.category2Short}
          disabled={!selectedCategory1}
          emptyOption={{ value: '', label: 'הכל' }}
          compact
        />
        <div className={`${styles.filterLabel} ${styles.filterLabelL2}`}>{FIELD_LABELS.category2Short}</div>

        <SearchableSelect
          value={selectedCategory3}
          onChange={setSelectedCategory3}
          options={optionsFromNames(availableCategories3)}
          ariaLabel={FIELD_LABELS.category3Short}
          disabled={!selectedCategory2}
          emptyOption={{ value: '', label: 'הכל' }}
          compact
        />
        <div className={`${styles.filterLabel} ${styles.filterLabelL3}`}>{FIELD_LABELS.category3Short}</div>

        <VendorSearchCombobox
          value={vendorSearch}
          options={uniqueVendors}
          onChange={setVendorSearch}
          aria-label={FIELD_LABELS.vendorName}
        />
        <div className={styles.filterLabel}>{FIELD_LABELS.vendorName}</div>

        <select
          value={submittedBy}
          onChange={(e) => setSubmittedBy(e.target.value)}
          className={styles.filterSelect}
          aria-label={FIELD_LABELS.submittedByShort}
        >
          <option value="">כל המשתמשים</option>
          {uniqueSubmittedBy.map((user) => (
            <option key={user} value={user}>{user}</option>
          ))}
        </select>
        <div className={styles.filterLabel}>{FIELD_LABELS.submittedByShort}</div>

        <label className={styles.creditFilterControl}>
          <input
            type="checkbox"
            checked={creditsOnly}
            onChange={(e) => setCreditsOnly(e.target.checked)}
            aria-label={FIELD_LABELS.credit}
          />
        </label>
        <div className={styles.filterLabel}>{FIELD_LABELS.credit}</div>
      </div>

      <div className={styles.filterDockRanges}>
        <div className={styles.filterDockRangeRow}>
          <div className={styles.filterDockRangeField}>
            <div className={styles.filterLabel}>מ</div>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className={styles.filterDockRangeInput}
              aria-label={`${FIELD_LABELS.documentDate} מ`}
            />
          </div>
          <div className={styles.filterDockRangeDivider} aria-hidden>
            –
          </div>
          <div className={styles.filterDockRangeField}>
            <div className={styles.filterLabel}>עד</div>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className={styles.filterDockRangeInput}
              aria-label={`${FIELD_LABELS.documentDate} עד`}
            />
          </div>
        </div>

        <div className={styles.filterDockRangeRow}>
          <div className={styles.filterDockRangeField}>
            <div className={styles.filterLabel}>מ</div>
            <input
              type="number"
              placeholder="0"
              value={amountMin}
              onChange={(e) => setAmountMin(e.target.value)}
              className={styles.filterDockRangeInput}
              aria-label={`${FIELD_LABELS.totalAmountShort} מ`}
            />
          </div>
          <div className={styles.filterDockRangeDivider} aria-hidden>
            –
          </div>
          <div className={styles.filterDockRangeField}>
            <div className={styles.filterLabel}>עד</div>
            <input
              type="number"
              placeholder="0"
              value={amountMax}
              onChange={(e) => setAmountMax(e.target.value)}
              className={styles.filterDockRangeInput}
              aria-label={`${FIELD_LABELS.totalAmountShort} עד`}
            />
          </div>
        </div>
      </div>

      {filterOverflowNote}
    </div>
  );

  const filterPanelBodyFloating = (
    <div className={`${styles.filterBar} ${styles.filterBarFloatingHorizontal}`}>
      <div className={styles.filterFloatField}>
        <div className={`${styles.filterLabel} ${styles.filterLabelL1}`}>{FIELD_LABELS.category1Short}</div>
        <SearchableSelect
          value={selectedCategory1}
          onChange={(next) => {
            setSelectedCategory1(next);
            setSelectedCategory2('');
            setSelectedCategory3('');
          }}
          options={optionsFromNames(flatCategories?.categories1 || [])}
          ariaLabel={FIELD_LABELS.category1Short}
          emptyOption={{ value: '', label: 'הכל' }}
          compact
        />
      </div>

      <div className={styles.filterFloatField}>
        <div className={`${styles.filterLabel} ${styles.filterLabelL2}`}>{FIELD_LABELS.category2Short}</div>
        <SearchableSelect
          value={selectedCategory2}
          onChange={(next) => {
            setSelectedCategory2(next);
            setSelectedCategory3('');
          }}
          options={optionsFromNames(availableCategories2)}
          ariaLabel={FIELD_LABELS.category2Short}
          disabled={!selectedCategory1}
          emptyOption={{ value: '', label: 'הכל' }}
          compact
        />
      </div>

      <div className={styles.filterFloatField}>
        <div className={`${styles.filterLabel} ${styles.filterLabelL3}`}>{FIELD_LABELS.category3Short}</div>
        <SearchableSelect
          value={selectedCategory3}
          onChange={setSelectedCategory3}
          options={optionsFromNames(availableCategories3)}
          ariaLabel={FIELD_LABELS.category3Short}
          disabled={!selectedCategory2}
          emptyOption={{ value: '', label: 'הכל' }}
          compact
        />
      </div>

      <div className={styles.filterFloatField}>
        <div className={styles.filterLabel}>{FIELD_LABELS.vendorName}</div>
        <VendorSearchCombobox
          value={vendorSearch}
          options={uniqueVendors}
          onChange={setVendorSearch}
          aria-label={FIELD_LABELS.vendorName}
        />
      </div>

      <div className={styles.filterFloatField}>
        <div className={styles.filterLabel}>{FIELD_LABELS.submittedByShort}</div>
        <select
          value={submittedBy}
          onChange={(e) => setSubmittedBy(e.target.value)}
          className={styles.filterSelect}
          aria-label={FIELD_LABELS.submittedByShort}
        >
          <option value="">כל המשתמשים</option>
          {uniqueSubmittedBy.map((user) => (
            <option key={user} value={user}>{user}</option>
          ))}
        </select>
      </div>

      <div className={styles.filterFloatField}>
        <div className={styles.filterLabel}>{FIELD_LABELS.credit}</div>
        <label className={styles.creditFilterControl}>
          <input
            type="checkbox"
            checked={creditsOnly}
            onChange={(e) => setCreditsOnly(e.target.checked)}
            aria-label={FIELD_LABELS.credit}
          />
        </label>
      </div>

      <div className={styles.filterFloatRange}>
        <div className={styles.filterLabel}>{FIELD_LABELS.documentDate}</div>
        <div className={styles.filterFloatRangeInputs}>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className={styles.filterDockRangeInput}
            aria-label={`${FIELD_LABELS.documentDate} מ`}
          />
          <span className={styles.filterDockRangeDivider} aria-hidden>–</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className={styles.filterDockRangeInput}
            aria-label={`${FIELD_LABELS.documentDate} עד`}
          />
        </div>
      </div>

      <div className={styles.filterFloatRange}>
        <div className={styles.filterLabel}>{FIELD_LABELS.totalAmountShort}</div>
        <div className={styles.filterFloatRangeInputs}>
          <input
            type="number"
            placeholder="0"
            value={amountMin}
            onChange={(e) => setAmountMin(e.target.value)}
            className={styles.filterDockRangeInput}
            aria-label={`${FIELD_LABELS.totalAmountShort} מ`}
          />
          <span className={styles.filterDockRangeDivider} aria-hidden>–</span>
          <input
            type="number"
            placeholder="0"
            value={amountMax}
            onChange={(e) => setAmountMax(e.target.value)}
            className={styles.filterDockRangeInput}
            aria-label={`${FIELD_LABELS.totalAmountShort} עד`}
          />
        </div>
      </div>

      {filterOverflowNote}
    </div>
  );

  const renderFilterChrome = (opts?: { dragHint?: string }) => (
    <header
      className={styles.filterDockChrome}
      onMouseDown={handleFilterDragStart}
    >
      <div className={styles.filterDockHeaderActions}>
        <button
          type="button"
          className={styles.filterDockIconBtn}
          onClick={handleExportToCSV}
          onMouseDown={(e) => e.stopPropagation()}
          data-tooltip="ייצוא ל־Excel"
          aria-label="ייצוא ל־Excel"
        >
          <img
            src={`${process.env.PUBLIC_URL}/excel-icon.png`}
            alt=""
            width={18}
            height={18}
            aria-hidden="true"
            draggable={false}
          />
        </button>
        <button
          type="button"
          className={styles.filterDockIconBtn}
          onClick={closeFilterPanel}
          onMouseDown={(e) => e.stopPropagation()}
          data-tooltip="נקה מסננים"
          aria-label="נקה מסננים"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M17.65 6.35A7.95 7.95 0 0 0 12 4V1L7 6l5 5V7c2.76 0 5 2.24 5 5a5 5 0 0 1-8.9 3.1L6.7 16.5A7.98 7.98 0 0 0 20 12c0-2.21-.9-4.21-2.35-5.65z"
              fill="currentColor"
            />
          </svg>
        </button>
      </div>
      <div className={styles.filterDockHeaderEnd}>
        <h2 className={styles.filterDockTitle}>{opts?.dragHint || 'מסננים'}</h2>
        <button
          type="button"
          className={styles.filterDockClose}
          onClick={closeFilterPanel}
          onMouseDown={(e) => e.stopPropagation()}
          aria-label="סגור מסננים"
          title="סגור"
        >
          ×
        </button>
      </div>
    </header>
  );

  return (
    <div className={`${styles.container} ${filterPanelDocked ? styles.containerWithFilterDock : ''}`.trim()}>
      {/* Snap UI on document.body so nothing clips it (Windows-style) */}
      {isDraggingFilter &&
        createPortal(
          <>
            <div className={styles.dockSnapEdgeHint} aria-hidden />
            <div
              className={`${styles.dockSnapPreview} ${dockPreviewVisible ? styles.dockSnapPreviewActive : ''}`.trim()}
              aria-hidden
            />
          </>,
          document.body
        )}

      {filterPanelDocked && (
        <aside
          ref={filterDockPanelRef}
          className={styles.filterDockPanel}
          role="dialog"
          aria-label="מסננים"
          style={
            dockPanelLayout
              ? {
                  top: dockPanelLayout.top,
                  left: FILTER_DOCK_LEFT_PX,
                  maxHeight: dockPanelLayout.maxHeight,
                }
              : {
                  top: 0,
                  left: FILTER_DOCK_LEFT_PX,
                  visibility: 'hidden',
                }
          }
        >
          {renderFilterChrome()}
          <div className={styles.filterDockBody}>{filterPanelBodyDocked}</div>
        </aside>
      )}

      <div className={styles.pageHeaderShell}>
        <div className={styles.pageHeaderColumn}>
          <div className={styles.topBarActions}>
            <button className={styles.addRowButton} onClick={handleAddNewExpense} type="button">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="currentColor"/>
                </svg>
                הוסף הוצאה
              </button>
            {rivhitDocsEnabled ? (
              <button className={styles.addRowButton} onClick={handleExportToCpa} type="button">
                ייצוא לרו״ח
              </button>
            ) : null}

            <div className={styles.filterButtonWrap} ref={filterButtonWrapRef}>
              <button
                className={`${styles.filterButton} ${styles.filterIconButton} ${showFilters ? styles.filterButtonActive : ''}`}
                onClick={handleFilterButtonClick}
                type="button"
                aria-expanded={showFilters}
                aria-label="הצג מסננים"
                title="הצג מסננים"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                  <path
                    d="M4 5h16l-6.5 7.5V19l-3 1.5v-8L4 5z"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinejoin="round"
                    fill="none"
                  />
                </svg>
              </button>

              {filterPanelFloating &&
                createPortal(
                  <div
                    ref={filterPanelRef}
                    className={[
                      styles.filterPopup,
                      styles.filterPopupDockLook,
                      styles.filterPopupHorizontal,
                      styles.filterPopupFloating,
                      isDraggingFilter ? styles.filterPopupDragging : '',
                      dockPreviewVisible ? styles.filterPopupSnapping : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    role="dialog"
                    aria-label="מסננים"
                    style={
                      dockPreviewVisible
                        ? {
                            left: FILTER_DOCK_LEFT_PX,
                            top: dockPanelLayout?.top ?? 80,
                            right: 'auto',
                            width: FILTER_DOCK_WIDTH_PX,
                            maxHeight: dockPanelLayout?.maxHeight ?? '70vh',
                            maxWidth: FILTER_DOCK_WIDTH_PX,
                            minWidth: FILTER_DOCK_WIDTH_PX,
                            height: 'auto',
                          }
                        : {
                            left: filterPanelPos?.left ?? 24,
                            top: filterPanelPos?.top ?? 96,
                            right: 'auto',
                            width: filterPanelSize.width,
                            height: filterPanelSize.height ?? 'auto',
                            minHeight: FILTER_FLOAT_MIN_HEIGHT,
                            maxWidth: 'none',
                            minWidth: FILTER_FLOAT_MIN_WIDTH,
                          }
                    }
                  >
                    {renderFilterChrome({
                      dragHint: dockPreviewVisible ? 'שחרר לעגינה' : 'מסננים',
                    })}
                    <div className={styles.filterDockBody}>
                      {dockPreviewVisible ? filterPanelBodyDocked : filterPanelBodyFloating}
                    </div>
                    {!dockPreviewVisible && (
                      <div
                        className={styles.filterPopupResizeHandle}
                        onMouseDown={handleFilterResizeStart}
                        role="presentation"
                        aria-hidden
                      />
                    )}
                  </div>,
                  document.body
                )}
            </div>
          </div>

          <h1 className={styles.tenantName}>
            {state.auth.tenant?.name || 'ניהול הוצאות'}
          </h1>

          <div className={styles.headerSpacer} aria-hidden="true" />
        </div>
      </div>

      {/* Summary strip — only when filters are docked (keeps table layout unchanged while floating) */}
      {filterPanelDocked && (
        <div
          className={`${styles.summaryPanel} ${styles.summaryPanelDocked}`.trim()}
        >
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>סה״כ שורות</span>
            <span className={styles.summarySep}>:</span>
            <span className={styles.summaryValue}>{summaryStats.totalLines}</span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>סה״כ כולל מע״מ</span>
            <span className={styles.summarySep}>:</span>
            <span className={styles.summaryValue}>{formatAmount(summaryStats.totalWithVAT)}</span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>סה״כ ללא מע״מ</span>
            <span className={styles.summarySep}>:</span>
            <span className={styles.summaryValue}>{formatAmount(summaryStats.totalNet)}</span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>סה״כ מע״מ</span>
            <span className={styles.summarySep}>:</span>
            <span className={styles.summaryValue}>{formatAmount(summaryStats.totalVAT)}</span>
          </div>
        </div>
      )}

      {/* Header stays put; only the rows scroll */}
      <div className={styles.tableShell} ref={tableContainerRef}>
        {loading && expenses.length === 0 ? (
          <div className={styles.loadingMessage} role="status">
            טוען הוצאות…
          </div>
        ) : sortedExpenses.length === 0 ? (
          <div className={styles.emptyState}>
            <h3>אין הוצאות להצגה</h3>
            <p>נסו לשנות מסננים או הוסיפו הוצאה חדשה.</p>
            <button className={styles.addRowButton} onClick={handleAddNewExpense} type="button">
              הוסף הוצאה
            </button>
          </div>
        ) : (
          <>
          <div className={styles.tableHeaderPane}>
          <table className={styles.table}>
            <colgroup>
              <col className={styles.colAction} />
              {!compactColumns && <col className={styles.colId} />}
              <col className={styles.colVendor} />
              <col className={styles.colAmount} />
              <col className={styles.colVat} />
              <col className={styles.colCategory} />
              <col className={styles.colCategory} />
              {!compactColumns && <col className={styles.colCategory} />}
              {!compactColumns && <col className={styles.colTag} />}
              <col className={styles.colSubmitted} />
              {!compactColumns && <col className={styles.colInvoice} />}
              <col className={styles.colDate} />
              <col className={styles.colUploadDate} />
              <col className={styles.colStatus} />
            </colgroup>
            <thead>
              <tr className={styles.headerRow}>
                <th className={`${styles.headerCell} ${styles.actionHeader}`} aria-label="עריכה" />
                {!compactColumns && renderSortableHeader('id', FIELD_LABELS.lineId)}
                {renderSortableHeader('vendorName', FIELD_LABELS.vendorName)}
                {renderSortableHeader('totalAmount', FIELD_LABELS.totalAmountShort)}
                {renderSortableHeader('vatAmount', FIELD_LABELS.vatAmountShort)}
                {renderSortableHeader('category1', FIELD_LABELS.category1Short, { extraClassName: styles.headerCategoryL1 })}
                {renderSortableHeader('category2', FIELD_LABELS.category2Short, { extraClassName: styles.headerCategoryL2 })}
                {!compactColumns && renderSortableHeader('category3', FIELD_LABELS.category3Short, { extraClassName: styles.headerCategoryL3 })}
                {!compactColumns && renderSortableHeader('projectTag', FIELD_LABELS.projectTagShort)}
                {renderSortableHeader('submittedBy', FIELD_LABELS.submittedByShort)}
                {!compactColumns && renderSortableHeader('invoiceNumber', FIELD_LABELS.invoiceNumberShort)}
                {renderSortableHeader('documentDate', FIELD_LABELS.documentDateShort)}
                {renderSortableHeader('creationTimestamp', FIELD_LABELS.creationTimestampShort)}
                <th
                  className={`${styles.headerCell} ${styles.statusHeader}`}
                  aria-label="סטטוס בדיקה"
                >
                  <ReviewStatusFilterDropdown
                    selected={selectedReviewStatuses}
                    onChange={setSelectedReviewStatuses}
                    variant="column"
                  />
                </th>
              </tr>
            </thead>
          </table>
          </div>
          <div className={styles.tableBodyPane} ref={tableBodyRef}>
          <table className={styles.table}>
            <colgroup>
              <col className={styles.colAction} />
              {!compactColumns && <col className={styles.colId} />}
              <col className={styles.colVendor} />
              <col className={styles.colAmount} />
              <col className={styles.colVat} />
              <col className={styles.colCategory} />
              <col className={styles.colCategory} />
              {!compactColumns && <col className={styles.colCategory} />}
              {!compactColumns && <col className={styles.colTag} />}
              <col className={styles.colSubmitted} />
              {!compactColumns && <col className={styles.colInvoice} />}
              <col className={styles.colDate} />
              <col className={styles.colUploadDate} />
              <col className={styles.colStatus} />
            </colgroup>
            <tbody>
              {paginatedExpenses.map((expense) => {
                const invoice = getInvoiceNumber(expense);
                const docDate = formatMetricaDate(expense.documentDate);
                return (
                <tr key={expense.id} className={styles.dataRow}>
                  <td className={styles.actionCell}>
                    <button
                      className={styles.editIconButton}
                      onClick={() => handleEditExpense(expense)}
                      type="button"
                      aria-label="עריכה"
                      title="עריכה"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <path
                          d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a.9959.9959 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"
                          fill="currentColor"
                        />
                      </svg>
                    </button>
                  </td>
                  {!compactColumns && (
                    <td className={`${styles.idCell} ${expense.isCredit ? styles.creditHighlight : ''}`.trim()}>
                      <TruncatedCellText text={expense.id} dir="ltr" />
                    </td>
                  )}
                  <td className={`${styles.vendorCell} ${expense.isCredit ? styles.creditHighlight : ''}`.trim()}>
                    <TruncatedCellText
                      text={expense.vendorName}
                      dir="auto"
                      className={expense.manuallyEdited?.vendorName ? styles.manuallyEditedText : undefined}
                    />
                  </td>
                  <td className={`${styles.amountCell} ${expense.isCredit ? styles.creditHighlight : ''}`.trim()}>
                    {!isShekel(expense.currency) && expense.originalTotalAmount ? (
                      <span
                        className={styles.fxMark}
                        title={formatForeignAmount(
                          currencyByCode(expense.currency).symbol,
                          expense.originalTotalAmount
                        )}
                      >
                        *
                      </span>
                    ) : null}
                    <TruncatedCellText
                      text={expense.totalAmount ? formatMoneyPlain(expense.totalAmount) : null}
                      dir="ltr"
                    />
                  </td>
                  <td className={`${styles.amountCell} ${expense.isCredit ? styles.creditHighlight : ''}`.trim()}>
                    <TruncatedCellText
                      text={expense.noVat ? NO_VAT_DISPLAY : expense.vatAmount ? formatMoneyPlain(expense.vatAmount) : null}
                      dir="ltr"
                    />
                  </td>
                  <td className={styles.categoryCell}>
                    {expense.vatPercentageFromTree &&
                    expense.vatPercentageCategoryLevel === 1 ? (
                      <VATEligibilityIndicator vatPercentage={expense.vatPercentage ?? 0} />
                    ) : null}
                    <TruncatedCellText
                      text={expense.category1}
                      className={`${styles.categoryL1Text} ${expense.manuallyEdited?.category1 ? styles.manuallyEditedText : ''}`.trim()}
                    />
                  </td>
                  <td className={styles.categoryCell}>
                    {expense.vatPercentageFromTree &&
                    expense.vatPercentageCategoryLevel === 2 ? (
                      <VATEligibilityIndicator vatPercentage={expense.vatPercentage ?? 0} />
                    ) : null}
                    <TruncatedCellText
                      text={expense.category2}
                      className={`${styles.categoryL2Text} ${expense.manuallyEdited?.category2 ? styles.manuallyEditedText : ''}`.trim()}
                    />
                  </td>
                  {!compactColumns && (
                    <td className={styles.categoryCell}>
                      {expense.vatPercentageFromTree &&
                      expense.vatPercentageCategoryLevel === 3 ? (
                        <VATEligibilityIndicator vatPercentage={expense.vatPercentage ?? 0} />
                      ) : null}
                      <TruncatedCellText
                        text={expense.category3}
                        className={`${styles.categoryL3Text} ${expense.manuallyEdited?.category3 ? styles.manuallyEditedText : ''}`.trim()}
                      />
                    </td>
                  )}
                  {!compactColumns && (
                    <td className={styles.categoryCell}>
                      <TruncatedCellText text={expense.projectTag?.trim() || null} dir="auto" />
                    </td>
                  )}
                  <td className={styles.submittedCell}>
                    <TruncatedCellText text={expense.submittedBy} />
                  </td>
                  {!compactColumns && (
                    <td className={styles.invoiceCell}>
                      <TruncatedCellText text={invoice || null} />
                    </td>
                  )}
                  <td className={styles.dateCell}>
                    <TruncatedCellText
                      text={docDate || null}
                      className={expense.manuallyEdited?.documentDate ? styles.manuallyEditedText : undefined}
                      dir="ltr"
                    />
                  </td>
                  <td className={styles.dateCell}>
                    <TruncatedCellText
                      text={expense.creationTimestamp ? formatMetricaDate(expense.creationTimestamp) : null}
                      dir="ltr"
                    />
                  </td>
                  <td className={styles.statusCell}>
                    <ReviewStatusDot expense={expense} />
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
          </div>
          </>
        )}
      </div>

      {/* Pagination Component */}
      <Pagination
        currentPage={safePage}
        totalPages={Math.max(1, totalPages)}
        onPageChange={handlePageChange}
        totalItems={sortedExpenses.length}
        itemsPerPage={rowsPerPage}
        startIndex={startIndex}
        endIndex={endIndex}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleRowsPerPageChange}
        loadProgress={loadProgress}
      />

      {recurrenceAlert && (
        <AlertDialog
          title="הוצאה חוזרת מסתיימת"
          message={`ההוצאה החוזרת עבור ${recurrenceAlert.vendorName || 'ספק'} תסתיים ב-\u2066${formatMetricaDate(recurrenceAlert.endDate)}\u2069. נותרו כשלושה חודשים.`}
          type="warning"
          confirmText="אישור"
          onClose={() => {
            const tenantId = String(state.auth.user?.tenant_id || state.auth.tenant?.id || 'default');
            markRecurrenceReminderShown(tenantId, recurrenceAlert.seriesId);
            setRecurrenceAlert(null);
          }}
        />
      )}

    </div>
  );
};

export default ExpenseTable; 